import { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    PieChart,
    Pie,
    Cell,
    Legend,
    LineChart,
    Line,
} from 'recharts';
import { getDashboardUserStats } from '../../../utils/admin/dashboardApi';

const defaultStats = {
    totalUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
    paidUsers: 0,
    thisMonthJoined: 0,
};

export default function SuperAdminStatsDashboard() {
    const [stats, setStats] = useState(defaultStats);
    const [monthlyJoinTrend, setMonthlyJoinTrend] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res = await getDashboardUserStats();
                if (cancelled) return;
                if (res?.status === true && res?.data) {
                    const d = res.data;
                    setStats({
                        totalUsers: d.totalUsers ?? 0,
                        activeUsers: d.activeUsers ?? 0,
                        inactiveUsers: d.inactiveUsers ?? 0,
                        paidUsers: d.paidUsers ?? 0,
                        thisMonthJoined: d.thisMonthJoined ?? 0,
                    });
                    setMonthlyJoinTrend(
                        Array.isArray(d.monthlyJoinedTrend) ? d.monthlyJoinedTrend : []
                    );
                } else {
                    toast.error(res?.message || 'Could not load dashboard stats');
                }
            } catch (e) {
                if (!cancelled) {
                    toast.error(e?.message || 'Could not load dashboard stats');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const userBreakdownChartData = useMemo(
        () => [
            { name: 'Total Users', value: stats.totalUsers },
            { name: 'Active Users', value: stats.activeUsers },
            { name: 'Inactive Users', value: stats.inactiveUsers },
            { name: 'Paid Users', value: stats.paidUsers },
            { name: 'This Month Joined', value: stats.thisMonthJoined },
        ],
        [stats]
    );

    const pieChartData = useMemo(
        () => [
            { name: 'Active Users', value: stats.activeUsers },
            { name: 'Inactive Users', value: stats.inactiveUsers },
            { name: 'Paid Users', value: stats.paidUsers },
        ],
        [stats]
    );

    const lineChartData = useMemo(
        () =>
            monthlyJoinTrend.map((row) => ({
                month: row.month || row.monthLabel || row.monthKey || '',
                joined: Number(row.joined) || 0,
            })),
        [monthlyJoinTrend]
    );

    const colors = {
        logoBlueDark: '#0b1f63',
        logoBlue: '#1f3f95',
        logoBlueLight: '#4d73d9',
        logoYellow: '#f7b500',
        logoYellowSoft: '#ffd86b',
        neutral: '#7b8494',
        grid: '#e7ebf3',
    };
    const pieColors = [colors.logoBlue, colors.logoBlueLight, colors.logoYellow];

    return (
        <>
            {loading && (
                <div className="text-center py-4 text-muted" role="status">
                    Loading dashboard metrics…
                </div>
            )}

            <div className={`row g-3 mb-4 ${loading ? 'opacity-50' : ''}`} aria-busy={loading}>
                <div className="col-md-6 col-xl-3">
                    <div className="card border-0 shadow-sm h-100" style={{ borderTop: `4px solid ${colors.logoBlueDark}` }}>
                        <div className="card-body">
                            <p className="mb-1 text-muted">Total Users</p>
                            <h3 className="mb-0" style={{ color: colors.logoBlueDark }}>{stats.totalUsers}</h3>
                            <p className="small text-muted mb-0 mt-1">Customers</p>
                        </div>
                    </div>
                </div>
                <div className="col-md-6 col-xl-3">
                    <div className="card border-0 shadow-sm h-100" style={{ borderTop: `4px solid ${colors.logoBlue}` }}>
                        <div className="card-body">
                            <p className="mb-1 text-muted">Total Active Users</p>
                            <h3 className="mb-0" style={{ color: colors.logoBlue }}>{stats.activeUsers}</h3>
                        </div>
                    </div>
                </div>
                <div className="col-md-6 col-xl-3">
                    <div className="card border-0 shadow-sm h-100" style={{ borderTop: `4px solid ${colors.neutral}` }}>
                        <div className="card-body">
                            <p className="mb-1 text-muted">Inactive Users</p>
                            <h3 className="mb-0" style={{ color: colors.neutral }}>{stats.inactiveUsers}</h3>
                        </div>
                    </div>
                </div>
                <div className="col-md-6 col-xl-3">
                    <div className="card border-0 shadow-sm h-100" style={{ borderTop: `4px solid ${colors.logoYellow}` }}>
                        <div className="card-body">
                            <p className="mb-1 text-muted">Paid Users</p>
                            <h3 className="mb-0" style={{ color: colors.logoYellow }}>{stats.paidUsers}</h3>
                        </div>
                    </div>
                </div>
                <div className="col-md-6 col-xl-3">
                    <div className="card border-0 shadow-sm h-100" style={{ borderTop: `4px solid ${colors.logoYellowSoft}` }}>
                        <div className="card-body">
                            <p className="mb-1 text-muted">This Month Joined</p>
                            <h3 className="mb-0" style={{ color: colors.logoYellowSoft }}>{stats.thisMonthJoined}</h3>
                        </div>
                    </div>
                </div>
            </div>

            <div className={`row g-3 ${loading ? 'opacity-50' : ''}`}>
                <div className="col-xl-7">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body">
                            <h5 className="mb-3">User Metrics Overview</h5>
                            <div style={{ width: '100%', height: 'clamp(220px, 45vw, 320px)' }}>
                                <ResponsiveContainer>
                                    <BarChart data={userBreakdownChartData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} />
                                        <XAxis dataKey="name" interval={0} angle={-18} textAnchor="end" height={70} />
                                        <YAxis allowDecimals={false} />
                                        <Tooltip />
                                        <Bar dataKey="value" fill={colors.logoBlue} radius={[6, 6, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-5">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body">
                            <h5 className="mb-3">Active vs Inactive vs Paid</h5>
                            <div style={{ width: '100%', height: 'clamp(220px, 45vw, 320px)' }}>
                                <ResponsiveContainer>
                                    <PieChart>
                                        <Pie
                                            data={pieChartData}
                                            dataKey="value"
                                            nameKey="name"
                                            cx="50%"
                                            cy="50%"
                                            outerRadius={100}
                                            label
                                        >
                                            {pieChartData.map((entry, index) => (
                                                <Cell key={entry.name} fill={pieColors[index % pieColors.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className={`row g-3 mt-1 ${loading ? 'opacity-50' : ''}`}>
                <div className="col-12">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body">
                            <h5 className="mb-3">Monthly Joined Trend</h5>
                            <p className="small text-muted mb-2">New customer sign-ups per month (last 12 months)</p>
                            <div style={{ width: '100%', height: 'clamp(210px, 42vw, 280px)' }}>
                                <ResponsiveContainer>
                                    <LineChart data={lineChartData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} />
                                        <XAxis dataKey="month" interval={0} angle={-22} textAnchor="end" height={64} />
                                        <YAxis allowDecimals={false} />
                                        <Tooltip />
                                        <Line
                                            type="monotone"
                                            dataKey="joined"
                                            stroke={colors.logoYellow}
                                            strokeWidth={3}
                                            dot={{ r: 4, fill: colors.logoYellow, stroke: colors.logoYellow }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
